import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
} from 'antd';
import {
  InfoTooltip,
  TextArea,
  TimeWithTimeZone,
  Icon,
  Text,
  Gap,
  Gutter,
} from '@common/components';
import {
  DoNotRehireType,
  ProfileStatusSetupDrawerEntityType,
} from '@working-experience/graphql';
import { useCurrentMembership } from '@identity/hooks';

const { Option } = Select;

type WorkerStatusType = 'eligible_for_resubmission' | 'do_not_rehire';

export type ProfileStatusSetupFormValuesType = {
  workerStatus: WorkerStatusType;
  institutionLevel: string;
  expirationIndefinite: boolean;
  expirationNumber?: number;
  expirationPeriod?: string;
  reason: string;
};

export function ProfileStatusSetupForm({
  entity,
  onSubmit,
  form,
  workerStatus,
  doNotRehireData: dnrData,
  onWorkerStatusChange,
  onFormChange,
  doNotRehireQueryParam,
  setFormHasErrors,
  assignmentCancellationReason,
}: {
  entity?: ProfileStatusSetupDrawerEntityType;
  onSubmit: (values: ProfileStatusSetupFormValuesType) => void;
  form: FormInstance;
  workerStatus?: WorkerStatusType;
  doNotRehireData?: DoNotRehireType;
  onWorkerStatusChange: (value: WorkerStatusType) => void;
  onFormChange: () => void;
  doNotRehireQueryParam: boolean;
  setFormHasErrors: (value: boolean) => void;
  assignmentCancellationReason?: string;
}) {
  const currentMembership = useCurrentMembership();
  const formDisabled = !currentMembership.canUpdateWorkerStatus;

  const [indefiniteExpiration, setIndefiniteExpiration] =
    useState<boolean>(false);

  const [expiration, setExpiration] = useState<string | undefined>();
  const [
    newExpirationBeforeCurrentEndDate,
    setNewExpirationBeforeCurrentEndDate,
  ] = useState<boolean>(false);

  useEffect(() => {
    form.setFieldsValue({
      workerStatus:
        dnrData || doNotRehireQueryParam
          ? 'do_not_rehire'
          : 'eligible_for_resubmission',
      institutionLevel: dnrData ? dnrData.institutionLevel : 'organization',
      reason: dnrData ? dnrData.reason : undefined,
      assignmentCancellationReason: assignmentCancellationReason || undefined,
      expirationIndefinite: dnrData?.expiration === 'indefinite',
      expirationNumber:
        dnrData && dnrData.expiration !== 'indefinite'
          ? dnrData.expiration?.split(' ')[0]
          : undefined,
      expirationPeriod:
        dnrData && dnrData.expiration !== 'indefinite'
          ? dnrData.expiration?.split(' ')[1]
          : 'years',
    });
    setExpiration(
      `${form.getFieldValue('expirationNumber')} ${form.getFieldValue(
        'expirationPeriod'
      )}`
    );
    setIndefiniteExpiration(dnrData?.expiration === 'indefinite');
  }, [
    form,
    dnrData,
    entity,
    doNotRehireQueryParam,
    assignmentCancellationReason,
  ]);

  const addEndDateError = (newEndDate: Date) => {
    const startDate = new Date(dnrData?.createdAt || '');

    if (
      (newEndDate > startDate || indefiniteExpiration) &&
      newExpirationBeforeCurrentEndDate
    ) {
      setNewExpirationBeforeCurrentEndDate(false);
      setFormHasErrors(false);
    }
    if (
      newEndDate <= startDate &&
      !newExpirationBeforeCurrentEndDate &&
      !indefiniteExpiration
    ) {
      setNewExpirationBeforeCurrentEndDate(true);
      setFormHasErrors(true);
    }
  };

  const buildExpirationDetailsEndDate = (
    startDate: string,
    period: string | undefined
  ): JSX.Element | string | undefined => {
    const newEndDate = new Date(startDate);
    if (!period) {
      return;
    }

    if (period.split(' ')[1] === 'years') {
      newEndDate.setFullYear(
        newEndDate.getFullYear() + parseInt(period.split(' ')[0])
      );
    } else {
      newEndDate.setMonth(
        newEndDate.getMonth() + parseInt(period.split(' ')[0])
      );
    }

    addEndDateError(newEndDate);

    if (indefiniteExpiration) {
      return 'Indefinite';
    }

    if (!form.getFieldValue('expirationNumber')) {
      return;
    }

    if (newExpirationBeforeCurrentEndDate && dnrData) {
      return (
        <>
          <TimeWithTimeZone
            date={dnrData.endsAt || ''}
            variant="short_format"
          />{' '}
          ({dnrData.expiration})
        </>
      );
    }

    return (
      <>
        <TimeWithTimeZone
          date={newEndDate.toISOString()}
          variant="short_format"
        />{' '}
        ({expiration})
      </>
    );
  };

  return (
    <Form
      className="ProfileStatusSetupForm"
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      onChange={onFormChange}
      disabled={formDisabled}
    >
      <Form.Item label="Worker Status" name="workerStatus">
        <Radio.Group
          defaultValue={workerStatus}
          size="large"
          onChange={(event) => onWorkerStatusChange(event.target.value)}
          disabled={formDisabled}
        >
          <Gap vertical>
            <Radio value="eligible_for_resubmission">
              Eligible for Resubmission
            </Radio>
            <Radio value="do_not_rehire">
              Do Not Rehire
              <InfoTooltip
                size="md"
                leftOffset="lg"
                title="Suppliers will need to provide specific reasons if they choose to resubmit this worker."
              />
            </Radio>
          </Gap>
        </Radio.Group>
      </Form.Item>

      {workerStatus === 'do_not_rehire' && (
        <>
          <Form.Item label="Level" name="institutionLevel">
            <Radio.Group
              defaultValue="organization"
              size="large"
              disabled={formDisabled}
            >
              <Gap vertical>
                <Radio value="organization">
                  All Units in {entity?.organization.name}
                </Radio>
                <Radio value="organization_business">
                  All Units in {entity?.organizationBusiness.name}
                </Radio>
                <Radio value="organization_unit">
                  Only {entity?.organizationUnit.name}
                </Radio>
              </Gap>
            </Radio.Group>
          </Form.Item>

          <Row gutter={[12, 0]}>
            <Col span={5}>
              <Form.Item
                label="Expiration"
                name="expirationNumber"
                className={`${dnrData && 'dnr-expiration'}`}
                rules={[
                  {
                    required: !indefiniteExpiration,
                    message: 'Please type the expiration',
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={12}
                  size="large"
                  disabled={indefiniteExpiration || formDisabled}
                  onChange={() => {
                    onFormChange();
                    setExpiration(
                      `${form.getFieldValue(
                        'expirationNumber'
                      )} ${form.getFieldValue('expirationPeriod')}`
                    );
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label={<></>}
                name="expirationPeriod"
                className={`${dnrData && 'dnr-expiration'}`}
              >
                <Select
                  size="large"
                  defaultValue="years"
                  disabled={indefiniteExpiration || formDisabled}
                  onChange={() => {
                    onFormChange();
                    setExpiration(
                      `${form.getFieldValue(
                        'expirationNumber'
                      )} ${form.getFieldValue('expirationPeriod')}`
                    );
                  }}
                >
                  <Option key="years" value="years">
                    years
                  </Option>
                  <Option key="months" value="months">
                    months
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                label={<></>}
                name="expirationIndefinite"
                className={`${dnrData && 'dnr-expiration'}`}
              >
                <Checkbox
                  onChange={(event) => {
                    setIndefiniteExpiration(event.target.checked);
                    form.setFieldsValue({
                      expirationIndefinite: event.target.checked,
                    });
                  }}
                  checked={indefiniteExpiration}
                  disabled={formDisabled}
                >
                  Indefinite
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          {dnrData && newExpirationBeforeCurrentEndDate && (
            <Text color="coral">
              Not valid due to the elapsed time from the start date.
            </Text>
          )}

          {dnrData && (
            <Gutter size="md" direction="bottom">
              <Gap>
                <Icon
                  icon="circleInfo"
                  kind="light"
                  size="md"
                  color="midnightLighter"
                  className="expiration-details-icon"
                />
                <Text secondary>
                  Start date:{' '}
                  <TimeWithTimeZone
                    date={dnrData.createdAt}
                    variant="short_format"
                  />{' '}
                  - End date:{' '}
                  {buildExpirationDetailsEndDate(dnrData.createdAt, expiration)}
                </Text>
              </Gap>
            </Gutter>
          )}

          {assignmentCancellationReason && (
            <Form.Item
              label="Assignment Cancellation Comment "
              name="assignmentCancellationReason"
            >
              <Input.TextArea disabled rows={3} />
            </Form.Item>
          )}

          <Form.Item
            label={
              <>
                {assignmentCancellationReason
                  ? 'Do Not Rehire Comment'
                  : 'Comment'}
                <InfoTooltip
                  size="md"
                  leftOffset="lg"
                  title="Only the supplier linked to this can view this comment."
                />
              </>
            }
            name="reason"
            rules={[
              {
                type: 'string',
                min: 1,
                max: 1000,
                required: true,
                message: 'Please enter a comment',
              },
            ]}
          >
            <TextArea
              disabled={formDisabled}
              placeholder="Please provide more details..."
              maxLength={1000}
            />
          </Form.Item>
        </>
      )}
    </Form>
  );
}
